// Compare tenses: one person and number across वर्तमान · विधि-आज्ञा · भूत · भविष्यत् (पढइ · पढउ · पढीअ · पढिहिइ),
// then the whole pattern as a 6 × 4 matrix. Adults also see the advanced tables of the same cell.

import type { FC, ReactNode } from 'react'
import type { Num, Person, Tense } from '@/prakrit/lib/grammar/types'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import { cn } from '@/lib/utils'
import { verbFormation } from '@/prakrit/lib/grammar/formation'
import {
    NUMBER_LABEL,
    PERSON_LABEL,
    SUBJECT,
    TENSE_LABEL,
    TENSE_SHORT
} from '@/prakrit/lib/grammar/labels'
import { Bi } from '@/prakrit/components/common/bi'
import { StatusBadge } from '@/prakrit/components/common/status-badge'
import { FormulaParts } from '@/prakrit/components/grammar/formation-steps'
import { SectionTitle } from '@/prakrit/components/grammar/labs/lab-ui'
import { CELL_ORDER, advancedTensesOf, basicTensesOf } from '@/prakrit/components/grammar/labs/verb-data'

interface TenseCompareProps {
    root: LabRoot
    person: Person
    number: Num
    tense: Tense
    showAlt: boolean
    /** Adults also compare the कर्मवाच्य, प्रेरणार्थक and क्रियातिपत्ति tables. */
    advanced: boolean
    onPickTense: (tense: Tense) => void
    onSelectCell: (person: Person, number: Num) => void
}

const formsOf = (root: LabRoot, tense: Tense, p: Person, n: Num) => {
    const cell = root.tables[tense]?.[p]?.[n] ?? []

    return {
        core: cell.filter(f => f.status === 'core').map(f => f.form),
        alt: cell.filter(f => f.status === 'alt').map(f => f.form)
    }
}

/** "पढि + हिमि" — the step just before the finished form. */
const endingStep = (root: LabRoot, tense: Tense, p: Person, n: Num, form: string) => {
    if (root.group === 'as' || !form) return null

    const f = verbFormation(root.root, root.rootClass, tense, p, n, form)

    return f.steps.length >= 2 ? f.steps[f.steps.length - 2].parts : null
}

const TenseCard: FC<{ root: LabRoot, tense: Tense, person: Person, number: Num, active: boolean, showAlt: boolean, onPick: () => void, small?: boolean }> = ({
    root,
    tense,
    person,
    number,
    active,
    showAlt,
    onPick,
    small = false
}): ReactNode => {
    const { core, alt } = formsOf(root, tense, person, number)
    const parts = endingStep(root, tense, person, number, core[0] ?? '')

    return (
        <button
            type='button'
            aria-pressed={active}
            onClick={onPick}
            className={cn(
                'flex h-full w-full flex-col items-start gap-2 rounded-xl border bg-card p-4 text-left shadow-card transition-colors',
                active ? 'border-prakrit/45 ring-2 ring-prakrit/20' : 'border-border hover:border-saffron/60'
            )}
        >
            <span className='flex flex-col leading-tight'>
                <span lang='hi' className='hindi text-sm font-semibold'>{TENSE_LABEL[tense].hi}</span>
                <span lang='en' className='text-xs text-muted-foreground'>{TENSE_LABEL[tense].en}</span>
            </span>
            <span lang='pra' className={cn('pra', small ? 'pra-md' : 'pra-lg')}>{core.join(' / ') || '—'}</span>
            {parts && parts.length > 1 && <FormulaParts parts={parts} className='text-base' />}
            {showAlt && alt.length > 0 && (
                <span className='flex flex-wrap items-center gap-1.5'>
                    <span lang='pra' className='font-prakrit text-sm text-terracotta'>{alt.join(', ')}</span>
                    <StatusBadge kind='alt' withEnglish={false} />
                </span>
            )}
        </button>
    )
}

export const TenseCompare: FC<TenseCompareProps> = ({ root, person, number, tense, showAlt, advanced, onPickTense, onSelectCell }): ReactNode => {
    const basic = basicTensesOf(root)
    const extra = advanced ? advancedTensesOf(root) : []
    const line = basic.map(t => formsOf(root, t, person, number).core[0]).filter(Boolean)

    return (
        <div className='flex flex-col gap-8'>
            <section className='flex flex-col gap-4'>
                <div className='flex flex-col gap-2'>
                    <p className='flex flex-wrap items-baseline gap-x-2 text-sm text-muted-foreground'>
                        <span lang='pra' className='pra pra-md'>{SUBJECT[person][number]}</span>
                        <span lang='hi' className='hindi font-medium text-foreground'>{PERSON_LABEL[person].hi} {NUMBER_LABEL[number].hi}</span>
                        <span lang='en' data-ex='en'>{PERSON_LABEL[person].en}, {NUMBER_LABEL[number].en}</span>
                    </p>
                    <p lang='pra' className='pra pra-xl flex flex-wrap gap-x-3'>
                        {line.map((f, i) => (
                            <span key={`${f}-${i}`} className='flex items-baseline gap-3'>
                                {i > 0 && <span aria-hidden='true' className='font-sans text-2xl font-normal text-muted-foreground/60'>·</span>}
                                {f}
                            </span>
                        ))}
                    </p>
                    <p className='text-sm text-muted-foreground'>
                        <Bi
                            hi='एक ही पुरुष और वचन, अलग-अलग काल — प्रत्यय बदलता है, धातु वही रहती है। किसी काल को चुनकर उसकी पूरी तालिका देखें।'
                            en='The same person and number in different tenses: the ending changes, the root stays. Pick a tense to open its full table.'
                        />
                    </p>
                </div>
                <ul className={cn('grid gap-3', basic.length > 2 ? 'sm:grid-cols-2 xl:grid-cols-4' : 'sm:grid-cols-2')}>
                    {basic.map(t => (
                        <li key={t}>
                            <TenseCard root={root} tense={t} person={person} number={number} active={t === tense} showAlt={showAlt} onPick={() => onPickTense(t)} />
                        </li>
                    ))}
                </ul>
            </section>

            {extra.length > 0 && (
                <section className='flex flex-col gap-3'>
                    <SectionTitle hi='आगे के रूप' en='Passive, causative and conditional' level='h3' />
                    <ul className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
                        {extra.map(t => (
                            <li key={t}>
                                <TenseCard root={root} tense={t} person={person} number={number} active={t === tense} showAlt={showAlt} onPick={() => onPickTense(t)} small />
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section className='flex flex-col gap-3'>
                <SectionTitle hi='पूरा ढाँचा' en='The whole pattern' level='h3'>
                    <p className='text-sm text-muted-foreground'>
                        <Bi hi='हर पंक्ति एक पुरुष-वचन है; पंक्ति चुनने के लिए बाईं ओर टैप करें।' en='Each row is one person and number; tap the left column to choose a row.' />
                    </p>
                </SectionTitle>
                <div className='overflow-x-auto rounded-xl border border-border bg-card shadow-card'>
                    <table className='w-full min-w-[34rem] border-collapse text-left'>
                        <caption className='sr-only'>{root.root}: {basic.map(t => TENSE_SHORT[t].hi).join(', ')}</caption>
                        <thead>
                            <tr className='border-b border-border bg-surface-muted/70'>
                                <th scope='col' className='px-3 py-2 text-xs font-semibold'>
                                    <Bi hi='पुरुष · वचन' en='person · number' className='leading-tight' always />
                                </th>
                                {basic.map(t => (
                                    <th key={t} scope='col' className='px-3 py-2 text-xs font-semibold'>
                                        <Bi hi={TENSE_SHORT[t].hi} en={TENSE_SHORT[t].en} className='leading-tight' always />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {CELL_ORDER.map(([p, n]) => {
                                const active = p === person && n === number

                                return (
                                    <tr key={`${p}.${n}`} className={cn('border-b border-border last:border-b-0', active && 'bg-saffron/8')}>
                                        <th scope='row' className='p-1.5 font-normal'>
                                            <button
                                                type='button'
                                                aria-pressed={active}
                                                onClick={() => onSelectCell(p, n)}
                                                className={cn('flex min-h-tap w-full flex-col items-start justify-center rounded-lg px-2 py-1 text-left hover:bg-saffron/10', active && 'ring-1 ring-prakrit/30')}
                                            >
                                                <span lang='pra' className='font-prakrit text-base font-semibold text-prakrit'>{SUBJECT[p][n]}</span>
                                                <span lang='hi' className='hindi text-xs text-muted-foreground'>{PERSON_LABEL[p].hi.replace(' पुरुष', '')} · {NUMBER_LABEL[n].hi}</span>
                                            </button>
                                        </th>
                                        {basic.map(t => (
                                            <td key={t} className={cn('px-3 py-2', t === tense && 'bg-prakrit/5')}>
                                                <span lang='pra' className='font-prakrit text-lg font-semibold text-prakrit'>{formsOf(root, t, p, n).core.join(' / ')}</span>
                                            </td>
                                        ))}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    )
}
